import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
    ModuleRegistry,
    AllCommunityModule,
    GridOptions,
    RowDragEndEvent,
    RowDragEvent
} from 'ag-grid-community';
import type { AgGridReact as AgGridReactType } from 'ag-grid-react';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);
const DEFAULT_TABLE_HEIGHT = 400;
const defaultGridOptions: IGridOptions = {
    columnDefs: [],
    rowData: [],
    defaultColDef: {
        sortable: true,
        filter: true,
    }
};

export interface IGridOptions<TData = any> extends GridOptions<TData> {
    onRowDragStart?: (event: RowDragEvent) => void;
    onRowDragMove?: (event: RowDragEvent) => void;
    onRowDragEnter?: (event: RowDragEvent) => void;
    onSimulatedRowDragEnd?: (event: { rowId: number, rowIndex: number }) => void;
}

export interface ISecuritiesDraggableTableProps {
    id: string;
    gridOptions?: IGridOptions;
    tableHeight?: number;
    rowData?: ISecuritiesTableFieldMap[]; 
}

export type SecuritiesTableFieldType =
    | "string"
    | "number"
    | "datetime"
    | "date"
    | "selectOne"
    | "selectMany"
    | "link"
    | "action"
    | "boolean"
    | "array"
    | "selectManyString";

export interface ISecuritiesTableField {
    type: SecuritiesTableFieldType;
    value: any;
    sortValue?: string | number | Date | boolean;
}

export interface ISecuritiesTableFieldMap {
    [name: string]: ISecuritiesTableField;
}

export interface SecuritiesDraggableTableRef {
    setFocusOnColumn: (rowIndex: number, colKey: string) => void;
}

const SecuritiesDraggableTable = forwardRef<SecuritiesDraggableTableRef, ISecuritiesDraggableTableProps>((
    { id,
        tableHeight = DEFAULT_TABLE_HEIGHT,
        gridOptions = defaultGridOptions,
        rowData = []
    }, ref) => {

    const gridRef = useRef<AgGridReactType>(null);
    const gridContainerRef = useRef<HTMLDivElement>(null); 
    const { animateRows, columnDefs, defaultColDef, onRowDragEnd, rowDragManaged, rowHeight, stopEditingWhenCellsLoseFocus, suppressMoveWhenRowDragging } = gridOptions;
    const rowId = useRef<number | null>(null);
    const lastRowIndex = useRef<number | null>(null);
    const isDragging = useRef(false);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
        setFocusOnColumn: (rowIndex: number, colKey: string) => {
            const api = gridRef.current?.api;
            if (api) {
                api.setFocusedCell(rowIndex, colKey);
                api.startEditingCell({ rowIndex, colKey });
            }
        },
    }));

    useEffect(() => {

        if (!gridOptions.onSimulatedRowDragEnd) {
             return;
        }

        const handleMouseUp = (e: MouseEvent): void => {
            if (isDragging.current) {
                isDragging.current = false;

                if (rowId.current && lastRowIndex.current) {
                    gridOptions.onSimulatedRowDragEnd?.({
                        rowId: rowId.current,
                        rowIndex: lastRowIndex.current
                    });
                }
            }
        };

        document.addEventListener('mouseup', handleMouseUp);
        return () => document.removeEventListener('mouseup', handleMouseUp);
    }, [gridOptions]);


    useEffect(() => {
        const gridElement = gridContainerRef.current;

        const handleFocusOut = (e: FocusEvent) => {
            const relatedTarget = e.relatedTarget as Node | null;

            if (gridElement && (!relatedTarget || !gridElement.contains(relatedTarget))) {
                gridRef.current?.api?.stopEditing();
            }
        };

        if (gridElement) {
            gridElement.addEventListener('focusout', handleFocusOut);
        }

        return () => {
            if (gridElement) {
                gridElement.removeEventListener('focusout', handleFocusOut);
            }
        };
    }, []);



    const handleRowDragStart = useCallback((event: RowDragEvent): void => {
        isDragging.current = true;
        rowId.current = (event.node?.data?.id as unknown as number) ?? null;
        lastRowIndex.current = event.node?.rowIndex ?? null;
        gridOptions.onRowDragStart?.(event);
    }, [gridOptions]);


    const handleRowDragMove = useCallback((event: RowDragEvent<ISecuritiesTableFieldMap>): void => {
        lastRowIndex.current = event.node?.rowIndex ?? null;
        gridOptions.onRowDragMove?.(event);
    }, [gridOptions]);


    const formattedRows = useMemo(() => {
        return rowData?.map(row => {
            const flattenedRow: Record<string, any> = {};
            for (const key in row) {
                flattenedRow[key] = row[key]?.value;
            }
            return flattenedRow;
        }) ?? [];
    }, [rowData]);

    const handleRowDragEnd = (event: RowDragEndEvent) => {
        isDragging.current = false;
        rowId.current = null;
        lastRowIndex.current = null;
        onRowDragEnd?.(event);
    };

    return (
        <div id={id} ref={gridContainerRef} className="ag-theme-alpine" style={{ height: tableHeight, width: '100%' }}>
            <AgGridReact
                ref={gridRef}
                rowData={formattedRows}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                rowHeight={rowHeight}
                rowDragManaged={rowDragManaged}
                suppressMoveWhenRowDragging={suppressMoveWhenRowDragging}
                animateRows={animateRows}
                stopEditingWhenCellsLoseFocus={stopEditingWhenCellsLoseFocus}
                onRowDragEnd={handleRowDragEnd}
                getRowId={(params) => params.data.id}
                onRowDragEnter={handleRowDragStart}
                onRowDragMove={handleRowDragMove}
            />
        </div>
    );

});

export default SecuritiesDraggableTable;
